import { CheckCircleFilled } from '@ant-design/icons'
import { Button } from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { VideoDirectorConceptV1 } from './videoDirectorTypes'

/** Compact proposal card with optional full Director Plan expansion. */

const SUMMARY_SECTIONS: Array<{ key: keyof VideoDirectorConceptV1; labelKey: string }> = [
  { key: 'creative_direction', labelKey: 'director.conceptCard.sections.creativeDirection' },
  { key: 'pacing', labelKey: 'director.conceptCard.sections.pacing' },
  { key: 'visual_style', labelKey: 'director.conceptCard.sections.visualStyle' },
  { key: 'camera_direction', labelKey: 'director.conceptCard.sections.cameraDirection' },
]

const DETAIL_SECTIONS: Array<{ key: keyof VideoDirectorConceptV1; labelKey: string }> = [
  { key: 'story_concept', labelKey: 'director.conceptCard.sections.storyConcept' },
  { key: 'performance_direction', labelKey: 'director.conceptCard.sections.performanceDirection' },
  { key: 'scene_direction', labelKey: 'director.conceptCard.sections.sceneDirection' },
  { key: 'continuity_constraints', labelKey: 'director.conceptCard.sections.continuityConstraints' },
  { key: 'identity_constraints', labelKey: 'director.conceptCard.sections.identityConstraints' },
  { key: 'platform_fit', labelKey: 'director.conceptCard.sections.platformFit' },
]

export default function DirectorConceptCard({
  concept,
  selected,
  onSelect,
  selectDisabled,
}: {
  concept: VideoDirectorConceptV1
  selected: boolean
  onSelect: () => void
  selectDisabled?: boolean
}) {
  const { t } = useTranslation('video')
  const [expanded, setExpanded] = useState(false)
  const summarySections = useMemo(() => SUMMARY_SECTIONS.map((s) => ({ ...s, label: t(s.labelKey) })), [t])
  const detailSections = useMemo(() => DETAIL_SECTIONS.map((s) => ({ ...s, label: t(s.labelKey) })), [t])

  return (
    <div
      className={`tc-vdc-concept-card${selected ? ' is-selected' : ''}`}
      data-testid={`director-concept-card-${concept.label}`}
    >
      <div className="tc-vdc-concept-header">
        <div className="tc-vdc-concept-header-main">
          <span className="tc-vdc-concept-badge">{t('director.conceptCard.proposal', { label: concept.label })}</span>
          <span className="tc-vdc-concept-title">{concept.title}</span>
          {selected && (
            <span className="tc-vdc-concept-selected-pill">
              <CheckCircleFilled /> {t('director.conceptCard.selected')}
            </span>
          )}
        </div>
        <div className="tc-vdc-concept-tagline">"{concept.tagline}"</div>
        {!!concept.style_tags?.length && (
          <div className="tc-vdc-concept-tags">
            {concept.style_tags.map((tag) => (
              <span key={tag} className="tc-vdc-concept-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="tc-vdc-concept-summary">{concept.summary}</div>

      <div className="tc-vdc-concept-meta-row">
        <span>{t('director.conceptCard.pacing')}：{concept.pacing || '—'}</span>
        <span>{t('director.conceptCard.estimatedShots')}：{concept.estimated_shot_count ?? '—'}</span>
        <span>{t('director.conceptCard.estimatedDuration', { seconds: concept.estimated_duration_seconds })}</span>
      </div>

      <div className="tc-vdc-concept-sections">
        {summarySections.map((section) => {
          const value = concept[section.key]
          if (!value) return null
          return (
            <div className="tc-vdc-concept-section" key={section.key}>
              <div className="tc-vdc-concept-section-label">{section.label}</div>
              <div className="tc-vdc-concept-section-value">{String(value)}</div>
            </div>
          )
        })}
        {concept.shot_outline && (
          <div className="tc-vdc-concept-section">
            <div className="tc-vdc-concept-section-label">{t('director.conceptCard.sections.shotOutline')}</div>
            <div className="tc-vdc-concept-section-value tc-vdc-preline">{concept.shot_outline}</div>
          </div>
        )}
      </div>

      {expanded && (
        <div className="tc-vdc-concept-sections is-expanded">
          {detailSections.map((section) => {
            const value = concept[section.key]
            if (!value) return null
            return (
              <div className="tc-vdc-concept-section" key={section.key}>
                <div className="tc-vdc-concept-section-label">{section.label}</div>
                <div className="tc-vdc-concept-section-value tc-vdc-preline">{String(value)}</div>
              </div>
            )
          })}
          {!!concept.shots?.length && (
            <div className="tc-vdc-concept-section">
              <div className="tc-vdc-concept-section-label">{t('director.conceptCard.sections.fullShotList')}</div>
              <div className="tc-vdc-concept-section-value">
                {(concept.shots as Array<Record<string, unknown>>).map((shot) => (
                  <div key={String(shot.shot_index)} className="tc-vdc-shot-row">
                    <strong>
                      Shot {String(shot.shot_index)} · {String(shot.duration_seconds)}s · {String(shot.shot_type)}
                    </strong>
                    <div>
                      {String(shot.framing)} / {String(shot.camera_movement)}
                    </div>
                    <div>{String(shot.subject_action)}</div>
                    {shot.visual_description ? <div>{String(shot.visual_description)}</div> : null}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="tc-vdc-concept-footer">
        <Button type="link" onClick={() => setExpanded((v) => !v)}>
          {expanded ? t('director.conceptCard.collapse') : t('director.conceptCard.expand')}
        </Button>
        <Button type={selected ? 'default' : 'primary'} onClick={onSelect} disabled={selected || selectDisabled}>
          {selected ? t('director.conceptCard.selectedThis') : t('director.conceptCard.select', { label: concept.label })}
        </Button>
      </div>
    </div>
  )
}
